<template>
  <MainCard
    title="Monitoramento de recursos"
    subtitle="Relatório de uso de tokens. Os gastos de tokens irão variar com base no modelo de IA utilizado."
    sb="Saiba Mais"
    link="https://rubeus.me/h6xvVA4"
  >
    <div class="resources-dashboard d-flex flex-column align-start mx-8 ga-8">
      <div v-if="finance.plan === 'free'" class="hiring-session ga-8 pa-4 d-flex align-center">
        <div>
          <h4>Quer utilizar todo o potencial do AI Studio?</h4>
          <p>Faça o upgrade para aproveitar os recursos sem se preocupar com limites. Quando seu limite gratuito for atingido, seus assistentes deixarão de funcionar.</p>
        </div>
        <v-btn
          class="px-6 py-5 d-flex"
          variant="flat"
          rounded
          color="primary-60"
          @click="openHiringPopUp"
        >
          Faça o upgrade
        </v-btn>
        <rb-popup
          v-model="hiringPopup"
          @confirm="hireAssistents"
          confirm-text="Contratar"
          cancel-text="Termos de uso"
          auxiliar-text="Cancelar"
          @cancel="termsOfUse"
          :close-on="['confirm', 'auxiliar' ,'exit']"
          :show-auxiliar="true"
          confirm-color="primary-60"
          cancel-color="primary-35"
          variant-cancel="outlined"
          width="600"
          variant-auxiliar="outlined"
          :button-styles="{
            cancel: { borderColor: '#c5c7c6'},
            auxiliar: { borderColor: '#c5c7c6' }
          }"
          title-class="d-flex px-8 mt-4 d-flex justify-end align-center"
          message-class="popUpCardText d-flex flex-column align-start pt-0 ga-4"
          :disabled-confirm="!user?.superadmin"
          :confirm-tooltip-text="!user?.superadmin ? 'Você precisa ter permissões de superadministrador para contratar' : ''"
        >
          <template #title><span></span></template>
          <template #message>
            <div class="d-flex ga-4">
              <img :src="require('@/ai_studio/assets/icons/assistent-logo.svg')" alt="" />
              <div class="vertical-divider"></div>
              <img :src="require('@/ai_studio/assets/icons/geracao-conteudo-logo.svg')" alt="" />
            </div>
            <p class="hiring-popup-text">
              O <strong>Assistentes</strong> e <strong>Geração de Conteúdo</strong> são contratados como um pacote de IAs generativas. Aproveite o poder da IA para criar Assistentes Virtuais personalizados e especializados, além de gerar automaticamente conteúdos para e-mails e comunicações.
             <a target="_blank" href="https://rubeus.com.br/solucoes/assistentes-rubeus/" @click="sendInteraction('interested')">Saiba Mais</a>
            </p>
            <v-list class="popup-list">
              <v-list-item class="popup-list-item">
                <template v-slot:prepend>
                  <img :src="require('@/ai_studio/assets/icons/assistent.svg')" alt="" />
                </template>
                <v-list-item-title>Crie assistentes virtuais de forma rápida e eficaz</v-list-item-title>
              </v-list-item>

              <v-list-item class="popup-list-item">
                <template v-slot:prepend>
                  <img :src="require('@/ai_studio/assets/icons/ai-stars.svg')" alt="" />
                </template>
                <v-list-item-title>Gere conteúdos para e-mails e comunicações com IA</v-list-item-title>
              </v-list-item>

              <v-list-item class="popup-list-item">
                <template v-slot:prepend>
                  <img :src="require('@/ai_studio/assets/icons/trending-up.svg')" alt="" />
                </template>
                <v-list-item-title>Aumente a captação e retenção de alunos com os modelos de predição</v-list-item-title>
              </v-list-item>
            </v-list>
          </template>
        </rb-popup>
      </div>

      <plan-usage-card />

      <div class="dashboard-container d-flex w-100 ga-8">
        <usage-chart
          title="Rubeus Assistente"
          :data="finance.chartData.assistents.data"
          :yAxisConfig="finance.chartData.assistents.yAxis"
          :loading="assistentsLoading"
          :gradient="['#eea88a', '#e34400']"
          :current-filter="assistentsFilter"
          @filter-change="fetchAssistentsData"
        />
        
        <usage-chart
          title="Geração de conteúdo"
          :data="finance.chartData.contentGeneration.data"
          :yAxisConfig="finance.chartData.contentGeneration.yAxis"
          :loading="contentLoading"
          :gradient="['#a9d4f1', '#070d62']"
          :current-filter="contentFilter"
          @filter-change="fetchContentData"
        />
      </div>
        
      <div class="monthly-finance-table mb-8" v-if="finance.plan === 'paid'">
        <v-data-table
          :headers="tableHeaders"
          :items="finance.monthlyFinances"
          :loading="tableLoading"
          class="elevation-1"
        >

        <template v-slot:loading>
            <div class="position-relative w-100 h-100">
              <v-skeleton-loader type="table-row@10" ></v-skeleton-loader>
              <rb-loader v-model='tableLoading' contained generic=""/>
            </div>
          </template>

          <template v-slot:[`item.month`]="{ item }">
            <div class="monthly-finance-table__values w-100" style="text-align: left;">
              <span>{{ item.month }}</span>
            </div>
          </template>

          <template v-slot:[`item.year`]="{ item }">
            <div class="monthly-finance-table__values w-100" style="text-align: left;">
              <span>{{ item.year }}</span>
            </div>
          </template>

          <template v-slot:[`item.total_tokens`]="{ item }">
            <div class="monthly-finance-table__values w-100" style="text-align: left;">
              <span>{{ item.total_tokens.toLocaleString('pt-BR') }}</span>
            </div>
          </template>

          <template v-slot:[`item.value_credits`]="{ item }">
            <div class="monthly-finance-table__values w-100" style="text-align: left;">
              <span>{{ formatCredits(item.value_credits) }}</span>
            </div>
          </template>
          <template v-slot:no-data>
            <div class="pa-8">
              <h3>Nenhum registro do consumo pago encontrado</h3>
              <p>
                O histórico do seu consumo será exibido aqui assim que houver uso dos tokens contratados.
              </p>
            </div>
          </template>
          <template v-slot:footer>
            <v-data-table-footer
              :items-per-page-options="[5, 10, 20, -1]"
              items-per-page-text="Itens por página"
              page-text="{0} de {1}"
              prev-icon="mdi-chevron-left"
              next-icon="mdi-chevron-right"
              first-icon="mdi-page-first"
              last-icon="mdi-page-last"
            />
          </template>
        </v-data-table>
      </div>
    </div>
  </MainCard>
</template>

<script setup>
import RbLoader from '@/ai_studio/components/common/RbLoader.vue';
import RbPopup from '@/ai_studio/components/common/RBPopup.vue';
import MainCard from '@/ai_studio/components/layout/MainCard.vue';
import PlanUsageCard from '@/assistents/components/PlanUsageCard.vue';
import UsageChart from '@/assistents/components/ChartComponent.vue';
import { useFinanceStore } from '@/assistents/stores/finance';
import { useUserStore } from '@/ai_studio/stores/user';
import { ref, onMounted, computed } from 'vue';
import axios from "axios";

const finance = useFinanceStore();
const userStore = useUserStore();
const user = computed(() => userStore.data)

const hiringPopup = ref(false);
const assistentsLoading = ref(false);
const contentLoading = ref(false);
const tableLoading = ref(false);

const assistentsFilter = ref('this_week');
const contentFilter = ref('this_week');

const tableHeaders = ref([
  { title: 'Mês', key: 'month', align: 'start', sortable: false, width: '64%' },
  { title: 'Ano', key: 'year', align: 'start', width: '10%' },
  { title: 'Total de tokens', key: 'total_tokens', align: 'start', width: '13%' },
  { title: 'Créditos', key: 'value_credits', align: 'start', width: '13%' },
]);

const formatCredits = (value) => {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue)) return '0.00';

  return numericValue.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const fetchAssistentsData = async (filterParams) => {
  assistentsFilter.value = filterParams.filter;
  assistentsLoading.value = true;
  await finance.fetchChartData('assistents', filterParams);
  assistentsLoading.value = false;
};

const fetchContentData = async (filterParams) => {
  contentFilter.value = filterParams.filter;
  contentLoading.value = true;
  await finance.fetchChartData('contentGeneration', filterParams);
  contentLoading.value = false;
};

const fetchTableData = async () => {
  tableLoading.value = true;
  await finance.fetchMonthlyFinances();
  tableLoading.value = false;
};

const hireAssistents = async () => {
  try {
    await axios.get('/api/assistents/resources/hire-assistents');
    await finance.fetchPlanData();
    await fetchAssistentsData({ filter: 'this_week' });
    await fetchContentData({ filter: 'this_week' });
    await fetchTableData();
    await sendInteraction('contracted');
  } catch (err) {
    console.error(err);
  }
};

const termsOfUse = async () => {
  window.open('https://rbarquivos.apprbs.com.br/aistudio/termosdeuso.pdf', '_blank')
};

const openHiringPopUp = () => {
  hiringPopup.value = !hiringPopup.value;
}

const sendInteraction = async (interaction_type) => {
    await axios.post('/api/assistents/resources/send-interaction', {
    'interaction_type': interaction_type,
    product: 'ai_studio'
  })
}

onMounted(async () => {
  await finance.fetchPlanData();
  if (finance.plan == 'paid') {
    await fetchTableData();
  }
});
</script>

<style lang="scss" scoped>
.resources-dashboard {
  width: 100%;
  color: rgb(var(--v-theme-neutral-15));
  font-family: Roboto;
  font-weight: 400;
  font-size: 16px;
}

.hiring-session {
  background-color: #f2f7f7;
  width: 810px;
  text-align: start;
  border-radius: 8px;
  line-height: 24px;
  letter-spacing: 0.5px;

  h4 {
    font-weight: 500;
  }

  p {
    font-weight: 400;
  }
}

.hiring-popup-text {
  font-size: 14px;
  color: rgb(var(--v-theme-neutral-15));
  text-align: start;

  a {
    font-weight: 700;
    text-decoration: none;
    color: rgb(var(--v-theme-primary-60));
  }
}

.popup-list {
  padding: 0;
  background: transparent;
}

.popup-list-item {
  padding-left: 0 !important;
  padding-right: 0 !important;
  min-height: auto !important;
  align-items: center;

  .v-list-item-title {
    font-size: 14px !important;
    color: rgb(var(--v-theme-neutral-15)) !important;
    text-align: start !important;
    line-height: 20px;
    font-weight: 400;
    white-space: normal;
  }

  .v-list-item__prepend {
    img {
      margin-right: 4px !important;
      width: 20px;
      height: 20px;
    }
  }
}

.monthly-finance-table {
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;

  :deep(.v-data-table__td) {
    color: rgb(var(--v-theme-neutral-15));
    font-size: 14px;
  }

  :deep(.v-data-table__th) {
    background-color: #F8FAF9;
  }

  :deep(.v-data-table-header__cell) {
    background-color: #FAFAFA;
    color: rgb(var(--v-theme-neutral-15)) !important;
    font-weight: 500;
    font-size: 14px;
  }

  :deep(.v-data-table-footer) {
    font-size: 12px;
  }
  .monthly-finance-table__values{
    font-family: Roboto;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.5px;
    color: rgb(var(--v-theme-neutral-35));
  }
}

.vertical-divider {
  width: 1px;
  height: 40px;
  background-color: #E1E3E2;
}
</style>
